/**
 * Aggressive cache buster - runs once on app load to clear old versions
 * This ensures users on old cached versions get the latest build
 */

import { storage } from './storage'
import { createLogger } from './logger'

const logger = createLogger('CacheBuster')

const CACHE_BUSTER_VERSION = '1.0.1' // Increment this to force cache clear
const CACHE_BUSTER_KEY = 'cacheBusterVersion'

/**
 * Check if cache needs to be cleared based on version
 */
export async function checkAndClearOldCache() {
  const currentVersion = storage.get(CACHE_BUSTER_KEY)
  
  // If version doesn't match, clear everything
  if (currentVersion !== CACHE_BUSTER_VERSION) {
    logger.info('Cache version mismatch', { currentVersion, expectedVersion: CACHE_BUSTER_VERSION })
    logger.info('Clearing old caches')
    
    await clearAllCaches()
    
    // Mark as cleared for this version
    storage.set(CACHE_BUSTER_KEY, CACHE_BUSTER_VERSION)
    logger.info('Cache cleared and version updated')
  } else {
    logger.info('Cache is up to date', { version: CACHE_BUSTER_VERSION })
  }
}

/**
 * Clear all browser caches and service workers
 */
async function clearAllCaches() {
  try {
    // Unregister service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations()
      for (const registration of registrations) {
        await registration.unregister()
        logger.info('Unregistered service worker', { scope: registration.scope })
      }
    }
    
    // Delete all caches
    if ('caches' in window) {
      const cacheNames = await caches.keys()
      for (const cacheName of cacheNames) {
        await caches.delete(cacheName)
        logger.info('Deleted cache', { cacheName })
      }
    }
    
    // Clear old version tracking
    storage.remove('buildHash')
    storage.remove('updatePromptShown')
    storage.remove('lastUpdatePrompt')
    
    logger.info('All caches cleared successfully')
  } catch (error) {
    logger.error('Error clearing caches', error)
  }
}

/**
 * Initialize cache buster - run this BEFORE React mounts
 */
export async function initCacheBuster() {
  // Only run in production
  if (import.meta.env.DEV) {
    return
  }
  
  logger.info('Initializing cache buster')
  await checkAndClearOldCache()
}
